import React from 'react';
import { ShoppingBag, Store, Briefcase, Palette, TrendingUp, Zap, Check, Users, DollarSign, Clock, Target, Award, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const UseCases = () => {
  const navigate = useNavigate();

  const useCases = [
    {
      icon: ShoppingBag,
      title: 'Etsy & Online Sellers',
      tagline: 'Stand Out in Crowded Marketplaces',
      description: 'Create unique, eye-catching designs that make your products stand out. Generate multiple variations to test what resonates with your audience.',
      image: 'https://images.unsplash.com/photo-1653669486634-cfac2c3f0937?w=600&h=400&fit=crop',
      gradient: 'from-cyan-500 to-blue-500',
      benefits: [
        'Generate 20+ designs in an hour',
        'Test different styles quickly',
        'Unique designs competitors can\'t copy',
        'Seasonal & trending designs on-demand',
        'Multiple variations for A/B testing',
      ],
      stats: [
        { icon: TrendingUp, value: '3x', label: 'Faster Production' },
        { icon: DollarSign, value: '50%', label: 'Cost Savings' },
        { icon: Target, value: '10+', label: 'Designs/Hour' },
      ],
    },
    {
      icon: Store,
      title: 'Print-on-Demand Businesses',
      tagline: 'Scale Your POD Empire',
      description: 'Build entire collections in hours, not weeks. Create cohesive design families and launch new products faster than ever.',
      image: 'https://images.unsplash.com/photo-1685883518316-355533810d68?w=600&h=400&fit=crop',
      gradient: 'from-turquoise-500 to-teal-500',
      benefits: [
        'Launch collections in days, not months',
        'Create design variations automatically',
        'High-resolution, print-ready files',
        'Commercial licensing included (Elite plan)',
        'Consistent quality across designs',
      ],
      stats: [
        { icon: Clock, value: '10x', label: 'Faster Launches' },
        { icon: Palette, value: '100+', label: 'Designs/Month' },
        { icon: Award, value: 'Pro', label: 'Quality Output' },
      ],
    },
    {
      icon: Briefcase,
      title: 'Design Agencies & Freelancers',
      tagline: 'Accelerate Your Creative Workflow',
      description: 'Use AI as your design partner. Quickly explore concepts, create client presentations, and deliver faster than competitors.',
      image: 'https://images.unsplash.com/photo-1753715613388-7e03410b1dce?w=600&h=400&fit=crop',
      gradient: 'from-purple-500 to-pink-500',
      benefits: [
        'Rapid concept exploration',
        'Client presentation mockups',
        'Version control for iterations',
        'Professional-quality outputs',
        'Competitive advantage',
      ],
      stats: [
        { icon: Users, value: '5x', label: 'More Clients' },
        { icon: Zap, value: '80%', label: 'Time Saved' },
        { icon: TrendingUp, value: '+40%', label: 'Revenue Boost' },
      ],
    },
  ];

  const additionalUseCases = [
    {
      icon: Palette,
      title: 'Hobby Creators',
      description: 'Make custom designs for personal projects, gifts, or small runs without hiring a designer.',
    },
    {
      icon: Users,
      title: 'Social Media Influencers',
      description: 'Launch your own merchandise line with unique designs that match your brand.',
    },
    {
      icon: TrendingUp,
      title: 'Startups & Brands',
      description: 'Create branded merchandise and promotional materials quickly and affordably.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Use Cases - Fluxara</title>
        <meta name="description" content="Discover how Fluxara helps different creators achieve their goals." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 gradient-mesh">
        {/* Hero */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              Perfect For
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600"> Every Creator</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're just starting out or scaling a business, Fluxara adapts to your needs
            </p>
          </div>
        </section>

        {/* Main Use Cases */}
        {useCases.map((useCase, index) => {
          const Icon = useCase.icon;
          const isEven = index % 2 === 0;
          
          return (
            <section key={index} className={`py-20 px-6 ${isEven ? 'bg-white' : 'bg-gradient-to-br from-gray-50 to-cyan-50'}`}>
              <div className="max-w-7xl mx-auto">
                <div className={`grid md:grid-cols-2 gap-12 items-center ${isEven ? '' : 'md:grid-flow-dense'}`}>
                  {/* Image */}
                  <div className={isEven ? '' : 'md:col-start-2'}>
                    <div className="relative group">
                      <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-turquoise-500 rounded-3xl blur opacity-25 group-hover:opacity-75 transition duration-500" />
                      <div className="relative">
                        <img 
                          src={useCase.image}
                          alt={useCase.title}
                          className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={isEven ? '' : 'md:col-start-1 md:row-start-1'}>
                    <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${useCase.gradient} rounded-2xl mb-6 shadow-xl`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-4xl font-black text-gray-900 mb-3">{useCase.title}</h2>
                    <p className="text-xl text-cyan-600 font-bold mb-6">{useCase.tagline}</p>
                    <p className="text-lg text-gray-700 leading-relaxed mb-8">{useCase.description}</p>

                    {/* Benefits */}
                    <div className="space-y-3 mb-8">
                      {useCase.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <Check className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      {useCase.stats.map((stat, idx) => {
                        const StatIcon = stat.icon;
                        return (
                          <div key={idx} className="glass p-4 rounded-xl text-center border-0">
                            <StatIcon className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                            <p className="text-2xl font-black text-gray-900">{stat.value}</p>
                            <p className="text-xs text-gray-600">{stat.label}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Additional Use Cases */}
        <section className="py-20 px-6 bg-gradient-to-br from-cyan-900 to-turquoise-900 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                And Many <span className="text-yellow-300">More...</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {additionalUseCases.map((useCase, index) => {
                const Icon = useCase.icon;
                return (
                  <div key={index} className="glass-dark p-8 rounded-2xl hover:scale-105 transition-transform duration-300">
                    <Icon className="w-12 h-12 text-cyan-300 mb-4" />
                    <h3 className="text-2xl font-bold mb-3">{useCase.title}</h3>
                    <p className="text-gray-300">{useCase.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              What Will <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-turquoise-600">You Create?</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join thousands of creators and start your journey today
            </p>
            <Button
              size="lg"
              className="text-xl px-12 py-8 bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700 shadow-2xl font-bold"
              onClick={() => navigate('/generate')}
            >
              <Sparkles className="mr-3 w-6 h-6" />
              Start Creating Now
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default UseCases;
